import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import emailIcon from '../../../assets/email.svg'
import passIcon from '../../../assets/pass.svg'
import AuthRightPanel from '../../../components/auth/AuthRightPanel'
import OnboardingCard from '../../../components/common/OnboardingCard'
import ErrorPopup from '../../../components/common/errorPopup'
import { useSupabase } from '../../../contexts/SupabaseContext'
import { supabase } from '../../../hooks/supabase'

export default function SignUp() {
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { signInWithOAuth } = useSupabase()
  const navigate = useNavigate()
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    try {
      // Create user with email/password
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            email_confirm: false
          }
        }
      })

      if (signUpError) {
        setError(signUpError.message)
        setIsErrorPopupOpen(true)
        return
      }

      // Store credentials for after email verification
      localStorage.setItem('tempAuthEmail', email)
      localStorage.setItem('tempAuthPassword', password)

      // Navigate to signin page with success message
      navigate({ 
        to: '/signin', 
        replace: true,
        search: {
          message: 'Verification email sent. Please check your inbox.'
        }
      })
    } catch (error) {
      console.error('Sign up error:', error)
      setError(error instanceof Error ? error.message : 'Failed to sign up')
      setIsErrorPopupOpen(true)
    }
  }

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col p-[16px]"> 
        <div className="mb-1">
          <div className="text-[32px] font-semibold mb-8 text-left">Sign up, build workspace</div>
          
          <div className="flex flex-col mb-6 border-b border-[#EEEFF1]">
            <button
              onClick={() => signInWithOAuth('google', '/')}
              className="w-full flex items-center justify-center gap-2 font-inter rounded-[10px] bg-[#FBFBFB] px-4 py-2.5 text-sm font-normal text-[#222222] h-[40px] mb-6 shadow-[0px_0px_2px_0px_#E0E0E0]"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-[20px] h-[20px]" />
              Sign in with Google
            </button>
          </div>

          <form onSubmit={handleSignUp} className="flex flex-col">
            <div className="space-y-3">
              <div className="relative">
                <img 
                  src={emailIcon} 
                  alt="Email" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5E63]"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your work email"
                  className="w-full h-[40px] pl-10 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>

              <div className="relative mb-7">
                <img 
                  src={passIcon} 
                  alt="Password" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5E63]"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create Password"
                  className="w-full h-[40px] pl-10 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-[40px] bg-[#29DC5B] mt-[28px] text-[#222222] rounded-[10px] flex items-center justify-center text-sm font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset]"
            >
              Continue
            </button>
          </form>
        </div>

        <div className="text-center text-sm font-light text-[#9FA1A7] mt-[24px]">
          Already using Clodexa? 
          <a href="/signin" className="text-[#222222] font-light ml-1 hover:underline">
            Sign in
          </a>
        </div>
      </div>

      <div className="mt-auto w-full flex justify-center">
        <div className="text-center w-[253px] text-xs text-[#9FA1A7]">
          By using Clodexa, you are agreeing to our 
          <a href="/privacy" className="text-[#222222] hover:underline mx-1">privacy policy</a>
          and
          <a href="/terms" className="text-[#222222] hover:underline ml-1">terms of service</a>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent}
        rightPanel={<AuthRightPanel />}
      />

      <ErrorPopup
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        message={error || 'An error occurred'}
      />
    </div>
  )
}
